import DOMPurify from "dompurify";

/**
 * Strict allowlist sanitizer for the places that render HTML from upstream
 * responses, STS history or message templates.
 *
 * The content is still parsed as HTML, so entities such as &nbsp; decode
 * exactly as they do today and the display is unchanged. Only tags and
 * attributes that could execute are removed.
 *
 * KEEP_CONTENT is on, so a stripped tag leaves its text behind — an <a> that
 * is removed still shows the address it wrapped, rather than vanishing.
 */

const ALLOWED_TAGS = [
  // text and emphasis — STS history and message templates
  "b", "strong", "i", "em", "u", "span", "p", "div", "br", "hr", "small", "font",
  // lists
  "ul", "ol", "li",
  // tables — the BuyLead contact panel and the enterprise script template
  "table", "thead", "tbody", "tfoot", "tr", "th", "td", "caption", "colgroup", "col",
  // headings
  "h1", "h2", "h3", "h4", "h5", "h6",
];

// `class` and `style` are kept because the existing markup uses them for
// layout and colour (e.g. the red enterprise warning, highlighted-Comment).
// DOMPurify parses and cleans style values, so url() and expression payloads
// do not survive.
const ALLOWED_ATTR = [
  "class", "style", "colspan", "rowspan", "align", "valign", "width", "height", "border", "cellpadding", "cellspacing",
];

export const sanitizeHtml = (dirty) => {
  if (typeof dirty !== "string" || dirty === "") return "";

  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
    FORBID_TAGS: ["script", "iframe", "object", "embed", "form", "input", "textarea", "button", "a", "img", "svg", "math", "link", "meta", "base"],
    FORBID_ATTR: ["href", "src", "srcset", "xlink:href", "action", "formaction", "background", "poster"],
    KEEP_CONTENT: true,
  });
};

/**
 * For the dangerouslySetInnerHTML prop, so call sites read as
 * `dangerouslySetInnerHTML={safeHtml(value)}` and cannot forget to sanitize.
 */
export const safeHtml = (dirty) => ({ __html: sanitizeHtml(dirty) });

export default sanitizeHtml;
